/**
 * Server ve web'in birlikte kullandığı zod şemaları.
 * Modül şemaları (kayıt, ilan formu, teklif ...) ilgili fazlarda buraya eklenir.
 */
import { z } from 'zod';
import { APPROVAL_STATUSES, COMPANY_TYPES, DEFAULT_PAGE_LIMIT, DELIVERY_FORMS, DELIVERY_OPTIONS, DEMAND_SORTS, DEMAND_STATUSES, LISTING_STATUSES, MAX_PAGE_LIMIT, OFFER_STATUSES, ORDER_SHIPMENT_STATUSES, PRICE_TYPES, RECURRENCES, LOCATION_BATCH_MAX, SAMPLE_DELIVERIES, SAMPLE_STATUSES, SAMPLE_VERDICTS, SHIPMENT_STATUSES, UNITS, VEHICLE_TYPES, } from './constants.js';
/** Prisma cuid kimlikleri. */
export const idSchema = z.string().min(1, 'Geçersiz kimlik.');
/** Türkçe karakterli slug (yalnızca ASCII küçük harf, rakam ve tire). */
export const slugSchema = z
    .string()
    .min(1)
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Geçersiz bağlantı adresi.');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'E-posta adresi zorunludur.')
    .max(254)
    .pipe(z.email('Geçerli bir e-posta adresi girin.'));
/** En az 8 karakter, harf ve rakam içermeli. */
export const passwordSchema = z
    .string()
    .min(8, 'Şifre en az 8 karakter olmalıdır.')
    .max(128, 'Şifre en fazla 128 karakter olabilir.')
    .regex(/[A-Za-zÇĞİÖŞÜçğıöşü]/, 'Şifre en az bir harf içermelidir.')
    .regex(/[0-9]/, 'Şifre en az bir rakam içermelidir.');
/** Türkiye telefon numarası: 10 hane (başında 0 olmadan), boşluk/tire temizlenir. */
export const phoneSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/[\s()-]/g, '').replace(/^(\+90|0090|0)/, ''))
    .refine((value) => /^[1-9][0-9]{9}$/.test(value), 'Geçerli bir telefon numarası girin.');
/** Vergi kimlik no (10 hane) veya T.C. kimlik no (11 hane). */
export const taxNumberSchema = z
    .string()
    .trim()
    .regex(/^[0-9]{10,11}$/, 'Vergi numarası 10 veya 11 haneli olmalıdır.');
/**
 * Para ve ondalık miktar alanları JSON'da STRING olarak taşınır (docs/02 §8).
 * Asla `number`'a çevirme; backend'de decimal.js ile hesapla.
 */
export const decimalStringSchema = z
    .string()
    .regex(/^-?\d+(\.\d{1,6})?$/, 'Geçersiz sayısal değer.');
/** Pozitif ondalık (miktar, fiyat). */
export const positiveDecimalStringSchema = decimalStringSchema.refine((value) => Number(value) > 0, "Değer 0'dan büyük olmalıdır.");
/** Yüzde alanı (KDV, komisyon, nem oranı). */
export const percentSchema = z.coerce.number().min(0).max(100);
/** Liste uçlarının ortak sayfalama parametreleri. */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT).default(DEFAULT_PAGE_LIMIT),
});
/**
 * Türkiye IBAN'ı: TR + 24 hane. Boşluklar temizlenir, ISO 13616 mod-97 sağlaması yapılır.
 * Satıcı ödemeleri için zorunlu (docs/06 §2.1).
 */
export const ibanSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, '').toUpperCase())
    .refine((value) => /^TR\d{24}$/.test(value), 'IBAN "TR" ile başlamalı ve 26 karakter olmalıdır.')
    .refine(isValidIbanChecksum, 'IBAN doğrulaması başarısız. Numarayı kontrol edin.');
/** ISO 13616 mod-97: ilk 4 karakter sona alınır, harfler sayıya çevrilir, kalan 1 olmalı. */
function isValidIbanChecksum(iban) {
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55));
    // Sayı JS'in güvenli tamsayı sınırını aştığı için parça parça mod alıyoruz.
    let remainder = 0;
    for (const digit of numeric) {
        remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
}
/**
 * T.C. kimlik numarası sağlaması: 11 hane, ilk hane 0 değil;
 * 10. hane = ((1+3+5+7+9. haneler) × 7 − (2+4+6+8. haneler)) mod 10,
 * 11. hane = ilk 10 hanenin toplamı mod 10.
 */
export function isValidNationalId(value) {
    if (!/^[1-9][0-9]{10}$/.test(value))
        return false;
    const d = [...value].map(Number);
    const tekler = d[0] + d[2] + d[4] + d[6] + d[8];
    const ciftler = d[1] + d[3] + d[5] + d[7];
    const onuncu = (((tekler * 7 - ciftler) % 10) + 10) % 10;
    if (onuncu !== d[9])
        return false;
    const toplam = d.slice(0, 10).reduce((sum, digit) => sum + digit, 0);
    return toplam % 10 === d[10];
}
/** Şoförün T.C. kimlik numarası (müşteri, 17.09.2026). */
export const nationalIdSchema = z
    .string()
    .trim()
    .refine(isValidNationalId, 'Geçerli bir T.C. kimlik numarası girin.');
/**
 * Türk araç plakası: il kodu (01-81), 1-3 harf, 2-5 rakam. Boşluklar ne girilirse
 * girilsin `34 AB 1234` biçimine getirilir — aynı araç iki farklı yazımla kaydolmasın.
 */
export const plateSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/[\s-]+/g, '').toUpperCase())
    .refine((value) => /^(0[1-9]|[1-7][0-9]|8[01])[A-Z]{1,3}[0-9]{2,5}$/.test(value), 'Geçerli bir plaka girin (ör. 34 AB 1234).')
    .transform((value) => value.replace(/^(\d{2})([A-Z]{1,3})(\d{2,5})$/, '$1 $2 $3'));
/**
 * Admin'in girdiği sipariş kodu. Biçim müşteride henüz kesin değil ("BIYO123456789"
 * olabilir): harf, rakam ve tire kabul edilir, büyük harfe çevrilir. Türkçe büyük harf
 * dönüşümü BİLEREK kullanılmıyor — "i" "İ" olur ve ASCII kod bozulur.
 */
export const orderCodeSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z0-9](?:[A-Z0-9-]{1,38}[A-Z0-9])$/.test(value), 'Sipariş kodu 3-40 karakter olmalı; yalnız harf, rakam ve tire içerebilir.');
/**
 * Sipariş kodunun firma parçası (ör. "VTR"): 2-6 harf/rakam.
 * Türkçe büyük harf dönüşümü BİLEREK yok — "i" "İ" olur ve ASCII kod bozulur. Türkçe
 * harf de kabul edilmez: kod irsaliyeye, Excel'e ve dosya adlarına giriyor (müşteri
 * Çimsa için "ÇMS" önerdi; sistemde "CMS").
 */
export const codePartSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z0-9]{2,6}$/.test(value), 'Kod 2-6 karakter olmalı; yalnız A-Z harfleri ve rakam (Ç, Ş, Ğ, Ü, Ö, İ olmaz).');
/**
 * Sipariş kodunun fabrika parçası: 1-6 harf/rakam. Müşteri fabrikaları firma
 * kısaltmasının yanına TEK RAKAMLA numaralıyor (02.10.2026: VTR + 1 = Hasanoğlan,
 * VTR + 2 = Yozgat) — harf kısaltması aynı harfle başlayan şehirlerde çakışıyordu.
 */
export const facilityCodePartSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z0-9]{1,6}$/.test(value), 'Fabrika kodu 1-6 karakter olmalı; yalnız A-Z harfleri ve rakam (ör. 1).');
/** Fabrikanın bize ilettiği sipariş numarası — kodun son parçası. */
export const customerOrderNoSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => /^[A-Z0-9](?:[A-Z0-9-]{0,28}[A-Z0-9])?$/.test(value), 'Sipariş numarası 1-30 karakter olmalı; harf, rakam ve tire içerebilir.');
/** `YYYY-MM-DD` takvim tarihi (saat dilimi taşımaz). */
export const isoDateSchema = z
    .string()
    .trim()
    .refine((value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    // 2026-02-30 gibi takvimde olmayan günler Date'te kayar; geri çevirip karşılaştırıyoruz.
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Geçerli bir tarih seçin.');
// ---------------------------------------------------------------------------
// Kimlik doğrulama (docs/04 §Auth)
// ---------------------------------------------------------------------------
const personNameSchema = z
    .string()
    .trim()
    .min(1, 'Ad soyad zorunludur.')
    .max(120, 'Ad soyad en fazla 120 karakter olabilir.');
const companyNameSchema = z
    .string()
    .trim()
    .min(1, 'Firma adı zorunludur.')
    .max(200, 'Firma adı en fazla 200 karakter olabilir.');
/** Boş bırakılan isteğe bağlı metin alanlarını `undefined` yapar. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value === undefined || value === '' ? undefined : value));
/** Kayıt: kullanıcı + firma tek istekte oluşturulur (docs/04 POST /auth/register). */
export const registerSchema = z.object({
    fullName: personNameSchema,
    email: emailSchema,
    password: passwordSchema,
    phone: phoneSchema,
    // Vergi kimlik no, vergi dairesi ve açık adres üyelikte SORULMUYOR (müşteri kararı):
    // kayıt sürtünmesini azaltmak için. Firma bunları sonradan profilinden tamamlar;
    // ödeme almadan önce vergi no zorunlu hâle gelir (`payments.service.ts`).
    company: z.object({
        name: companyNameSchema,
        type: z.enum(COMPANY_TYPES),
        // Form select'leri sayısal değer gönderir (valueAsNumber); coerce'a gerek yok,
        // böylece şemanın girdi ve çıktı tipleri aynı kalır ve form tipleri sadeleşir.
        provinceId: z.number().int().positive('İl seçiniz.'),
        districtId: z.number().int().positive('İlçe seçiniz.'),
        phone: phoneSchema,
    }),
    acceptTerms: z
        .boolean()
        .refine((value) => value === true, 'Kullanım koşullarını kabul etmelisiniz.'),
    /**
     * KVKK açık rızası koşul kabulünden AYRI alınır (docs/07 Faz 7 madde 6):
     * mevzuat, rızanın başka bir onayla birlikte "paket" olarak alınmasını kabul etmez.
     */
    acceptPrivacy: z
        .boolean()
        .refine((value) => value === true, 'Aydınlatma metnini onaylamanız gerekir.'),
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Şifre zorunludur.'),
});
export const verifyEmailSchema = z.object({
    token: z.string().min(10, 'Geçersiz doğrulama bağlantısı.'),
});
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(10, 'Geçersiz sıfırlama bağlantısı.'),
    password: passwordSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Mevcut şifrenizi girin.'),
    newPassword: passwordSchema,
});
export const resendVerificationSchema = z.object({
    email: emailSchema,
});
// ---------------------------------------------------------------------------
// Firma profili (docs/04 §Companies)
// ---------------------------------------------------------------------------
/** PATCH /companies/me — tüm alanlar isteğe bağlı, gönderilenler güncellenir. */
export const updateCompanySchema = z
    .object({
    name: companyNameSchema.optional(),
    // Üyelikte sorulmuyor; firma buradan tamamlıyor. Kimlik alanı olduğu için
    // değişmesi onayı yeniden tetikler (IDENTITY_FIELDS).
    taxNumber: taxNumberSchema.optional(),
    taxOffice: optionalText(120),
    type: z.enum(COMPANY_TYPES).optional(),
    provinceId: z.number().int().positive().optional(),
    districtId: z.number().int().positive().optional(),
    address: optionalText(500),
    phone: phoneSchema.optional(),
    website: z
        .union([z.literal(''), z.url('Geçerli bir web adresi girin.')])
        .optional()
        .transform((value) => (value === '' || value === undefined ? undefined : value)),
    about: optionalText(2000),
    iban: z
        .union([z.literal(''), ibanSchema])
        .optional()
        .transform((value) => (value === '' || value === undefined ? undefined : value)),
})
    .refine((value) => (value.provinceId === undefined) === (value.districtId === undefined), 'İl ve ilçe birlikte güncellenmelidir.');
/** PATCH /users/me — kişisel bilgiler ve bildirim tercihi. */
export const updateProfileSchema = z.object({
    fullName: personNameSchema.optional(),
    phone: phoneSchema.optional(),
    emailNotificationsEnabled: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Kategori kriterleri (docs/09 §1.4)
// ---------------------------------------------------------------------------
/**
 * İlanın bir kritere verdiği değer. Hangi kriterin hangi kategoriye ait olduğunu ve
 * zorunlu olup olmadığını yalnız veritabanı bildiği için asıl doğrulama servis
 * katmanında; şema burada yalnız taşıma biçimini sabitliyor.
 */
export const listingAttributeSchema = z.object({
    key: z.string().trim().min(1).max(60),
    value: z.union([z.string().trim().max(200), z.number(), z.boolean()]),
});
export const listingAttributesSchema = z.array(listingAttributeSchema).max(40).optional();
// ---------------------------------------------------------------------------
// Kayıtlı arama ve uyarı (docs/09 §1.6)
// ---------------------------------------------------------------------------
/**
 * Kayıtlı arama. Müşterinin istediği üç takip de bu tek kayıtla karşılanıyor:
 * yalnız satıcı seçilirse "favori satıcı", kategori+kriter seçilirse "favori ürün",
 * uzaklık verilirse "x km çevre" uyarısı olur.
 */
export const savedSearchSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, 'Arama adı zorunludur.')
        .max(120, 'Arama adı en fazla 120 karakter olabilir.'),
    q: optionalText(120),
    categoryId: idSchema.optional(),
    sellerCompanyId: idSchema.optional(),
    provinceId: z.number().int().positive().optional(),
    /** 100'ün katı: mesafeler zaten 100 km'lik kovalarda gösteriliyor. */
    maxDistanceKm: z.number().int().positive().max(3000).optional(),
    facilityId: idSchema.optional(),
    attributeFilters: z.array(z.string().trim().max(200)).max(40).default([]),
    notifyInApp: z.boolean().default(true),
    notifyEmail: z.boolean().default(true),
})
    .refine((value) => Boolean(value.q ||
    value.categoryId ||
    value.sellerCompanyId ||
    value.provinceId ||
    value.maxDistanceKm ||
    value.attributeFilters.length > 0), 
// Boş bir arama her yeni ilanda bildirim üretirdi; kullanıcı bunu istemez.
{ error: 'En az bir arama ölçütü seçmelisiniz.' });
// ---------------------------------------------------------------------------
// Tesis / fabrika (docs/09 §1.2)
// ---------------------------------------------------------------------------
/**
 * Alıcı firmanın satın alma yaptığı lokasyon. Bir kullanıcı birden fazla fabrikadan
 * sorumlu olabildiği için tesisler firmanın altında ayrı kayıtlar hâlinde durur;
 * aktif tesis mesafeyi ve yakınlık sıralamasını belirler.
 */
export const facilitySchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, 'Tesis adı zorunludur.')
        .max(120, 'Tesis adı en fazla 120 karakter olabilir.'),
    provinceId: z.number().int().positive('İl seçiniz.'),
    districtId: z.number().int().positive('İlçe seçiniz.'),
    address: optionalText(500),
    /** İşaretlenirse diğer tesislerin varsayılanı kalkar. */
    isDefault: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// İlan (docs/01 §5 tablosu — zorunluluk ve limitler birebir)
// ---------------------------------------------------------------------------
/**
 * Miktar/fiyat alanları formda metin olarak girilir, JSON'da string taşınır ve
 * backend'de Decimal'e çevrilir. Virgüllü giriş ("12,5") noktaya normalize edilir.
 */
const decimalInputSchema = (message) => z
    .string()
    .trim()
    .transform((value) => value.replace(',', '.'))
    .refine((value) => /^\d+(\.\d{1,3})?$/.test(value) && Number(value) > 0, message);
/** Boş bırakılabilen ondalık alan (min. sipariş, nem oranı). */
const optionalDecimalInputSchema = (message) => z
    .string()
    .trim()
    .transform((value) => value.replace(',', '.'))
    .transform((value) => (value === '' ? undefined : value))
    .refine((value) => value === undefined || (/^\d+(\.\d{1,3})?$/.test(value) && Number(value) >= 0), message);
/**
 * Boş bırakılabilen VEYA hiç gönderilmeyen ondalık alan.
 * İlan formunda alan her zaman gönderildiği için `optionalDecimalInputSchema` boş metni
 * bekler; API gövdesinde alan tamamen atlanabildiğinden burada `.optional()` ekleniyor.
 */
const omittableDecimalSchema = (message) => optionalDecimalInputSchema(message).optional();
const optionalPositiveIntSchema = z
    .union([z.literal(''), z.number().int().positive(), z.nan()])
    .optional()
    .transform((value) => value === '' || value === undefined || (typeof value === 'number' && Number.isNaN(value))
    ? undefined
    : value);
export const listingBaseSchema = z.object({
    title: z
        .string()
        .trim()
        .min(1, 'Başlık zorunludur.')
        .max(120, 'Başlık en fazla 120 karakter olabilir.'),
    categoryId: idSchema,
    description: z
        .string()
        .trim()
        .min(1, 'Açıklama zorunludur.')
        .max(5000, 'Açıklama en fazla 5000 karakter olabilir.'),
    quantity: decimalInputSchema('Geçerli bir miktar girin.'),
    unit: z.enum(UNITS),
    recurrence: z.enum(RECURRENCES),
    priceType: z.enum(PRICE_TYPES),
    /** FIXED ise zorunlu, NEGOTIABLE ise boş bırakılır (aşağıdaki refine kontrol eder). */
    unitPrice: optionalDecimalInputSchema('Geçerli bir birim fiyat girin.'),
    minOrderQty: optionalDecimalInputSchema('Geçerli bir minimum sipariş miktarı girin.'),
    /*
     * Ürün özellikleri (nem, kül, azot, kalori, atık kodu, taşıma formu, kırıcı) İSTEĞE
     * BAĞLI: "Ürün Özellikleri" bölümü İlan Ver formundan kaldırıldı (müşteri, 12.10.2026).
     * Düzenlemede form kayıtlı değerleri geri gönderdiği için eski ilanların değeri korunur;
     * API istemcileri yine gönderebilir.
     */
    moisturePct: omittableDecimalSchema('Nem oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Nem oranı 0 ile 100 arasında olmalıdır.'),
    /**
     * Kül oranı (%) — müşterinin ikincil filtre listesinde.
     * Atlanabilir: bu alanlar formdan sonra eklendi, mevcut API istemcileri göndermiyor.
     */
    ashPct: omittableDecimalSchema('Kül oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Kül oranı 0 ile 100 arasında olmalıdır.'),
    /** Azot (N) oranı (%) — müşterinin ikincil filtre listesinde. */
    nitrogenPct: omittableDecimalSchema('Azot oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Azot oranı 0 ile 100 arasında olmalıdır.'),
    /** Teslimat formu — balya, dökme, big bag. */
    deliveryForm: z.enum(DELIVERY_FORMS).optional(),
    /** Ürünün kırıcıdan geçirilmesi gerekiyor mu. */
    crusherRequired: z.boolean().optional(),
    /** İsteğe bağlı (12.10.2026); boş metin ve NaN `undefined` olur. */
    calorificValue: z.preprocess((val) => val === '' ||
        val === undefined ||
        val === null ||
        (typeof val === 'number' && Number.isNaN(val))
        ? undefined
        : Number(val), z
        .number({ error: 'Geçerli bir kalori değeri girin.' })
        .int('Kalori değeri tamsayı olmalıdır.')
        .positive('Geçerli bir kalori değeri girin.')
        .optional()),
    wasteCode: optionalText(20),
    deliveryOptions: z
        .array(z.enum(DELIVERY_OPTIONS))
        .min(1, 'En az bir teslimat şekli seçin.')
        .max(DELIVERY_OPTIONS.length),
    provinceId: z.number().int().positive('İl seçiniz.'),
    districtId: z.number().int().positive('İlçe seçiniz.'),
    /**
     * Kategoriye bağlı ürün kriterleri (docs/09 §1.4). Hangi kriterin bu kategoriye ait
     * olduğu ve zorunluluğu veritabanında durduğu için asıl doğrulama servis katmanında.
     */
    attributes: listingAttributesSchema,
});
/** PRD §5: "Teklife açık" seçilirse fiyat gizlenir, sabit fiyatta zorunludur. */
export const createListingSchema = listingBaseSchema
    .refine((value) => value.priceType !== 'FIXED' || value.unitPrice !== undefined, {
    path: ['unitPrice'],
    error: 'Sabit fiyat seçtiğinizde birim fiyat zorunludur.',
})
    .refine((value) => value.priceType !== 'NEGOTIABLE' || value.unitPrice === undefined, {
    path: ['unitPrice'],
    error: '"Teklife açık" ilanlarda birim fiyat girilmez.',
});
/** Güncellemede de aynı fiyat kuralları geçerli — form tüm alanları gönderir. */
export const updateListingSchema = createListingSchema;
/** Görsel sıralaması: gönderilen dizinin ilk elemanı kapak olur. */
export const reorderImagesSchema = z.object({
    imageIds: z.array(idSchema).min(1, 'En az bir görsel gerekir.'),
});
/** GET /listings/me — panel tablosu filtreleri. */
export const myListingsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(LISTING_STATUSES).optional(),
});
// ---------------------------------------------------------------------------
// Vitrin araması (docs/04 §Listings GET)
// ---------------------------------------------------------------------------
/**
 * Sıralama seçenekleri. `distance`, `quantity` ve `calorific` müşterinin istediği
 * ölçütler (docs/09 §1.5); yakınlık yalnız aktif tesisi olan alıcıda anlamlıdır,
 * diğerlerinde en yeniye düşer.
 */
export const LISTING_SORTS = [
    /**
     * Varsayılan sıra: görseli olan ve miktarı yüksek ilanlar önce (docs/09 §1.4).
     * Arama terimi varsa alaka düzeyi öne geçer.
     */
    'featured',
    'newest',
    'price_asc',
    'price_desc',
    'distance',
    'quantity_desc',
    /** Artan sıralar müşteri isteğiyle eklendi (12.10.2026). */
    'quantity_asc',
    'calorific_desc',
    'calorific_asc',
];
/** Sorgu dizesinde tek değer de virgüllü liste de kabul edilir: `unit=TON,KG`. */
const csvEnum = (values) => z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
    if (value === undefined)
        return undefined;
    const list = (Array.isArray(value) ? value : value.split(','))
        .map((item) => item.trim())
        .filter(Boolean);
    return list.length > 0 ? list : undefined;
})
    .refine((list) => list === undefined || list.every((item) => values.includes(item)), 'Geçersiz filtre değeri.')
    .transform((list) => list);
const optionalPositiveNumber = z
    .union([z.string(), z.number()])
    .optional()
    .transform((value) => {
    if (value === undefined || value === '')
        return undefined;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
});
/** `evet`/`hayir` biçiminde gelen üçlü filtre: seçilmemiş, evet, hayır. */
const optionalBooleanFilter = z
    .union([z.string(), z.boolean()])
    .optional()
    .transform((value) => {
    if (value === undefined || value === '')
        return undefined;
    if (typeof value === 'boolean')
        return value;
    if (value === 'evet' || value === 'true' || value === '1')
        return true;
    if (value === 'hayir' || value === 'false' || value === '0')
        return false;
    return undefined;
});
const optionalPositiveInt = z
    .union([z.string(), z.number()])
    .optional()
    .transform((value) => {
    if (value === undefined || value === '')
        return undefined;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
});
const attributeFilterSchema = z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
    if (value === undefined)
        return undefined;
    const entries = Array.isArray(value) ? value : [value];
    const byKey = new Map();
    for (const entry of entries) {
        const separator = entry.indexOf(':');
        if (separator <= 0)
            continue;
        const key = entry.slice(0, separator).trim();
        const raw = entry.slice(separator + 1).trim();
        if (!key || !raw)
            continue;
        const filter = byKey.get(key) ?? { key, values: [] };
        const range = /^(-?[\d.,]*)\.\.(-?[\d.,]*)$/.exec(raw);
        if (range) {
            const min = Number((range[1] ?? '').replace(',', '.'));
            const max = Number((range[2] ?? '').replace(',', '.'));
            if (range[1] && Number.isFinite(min))
                filter.min = min;
            if (range[2] && Number.isFinite(max))
                filter.max = max;
        }
        else {
            filter.values.push(raw);
        }
        byKey.set(key, filter);
    }
    const filters = [...byKey.values()].filter((filter) => filter.values.length > 0 || filter.min !== undefined || filter.max !== undefined);
    return filters.length > 0 ? filters : undefined;
});
export const listingSearchQuerySchema = paginationQuerySchema.extend({
    /** Tam metin arama terimi (PostgreSQL `turkish` konfigürasyonu). */
    q: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((value) => (value === undefined || value === '' ? undefined : value)),
    /** Alt kategori seçilirse yalnız o, ana kategori seçilirse altları da dahil edilir. */
    categoryId: idSchema.optional(),
    categorySlug: z.string().trim().max(160).optional(),
    provinceId: optionalPositiveInt,
    priceMin: optionalPositiveNumber,
    priceMax: optionalPositiveNumber,
    unit: csvEnum(UNITS),
    delivery: csvEnum(DELIVERY_OPTIONS),
    priceType: csvEnum(PRICE_TYPES),
    /** Kategoriye bağlı kriter filtreleri — yukarıdaki `oz` biçimi. */
    oz: attributeFilterSchema,
    /**
     * En fazla uzaklık (km). Alıcının aktif tesisinden ölçülür; oturumsuz ziyaretçide
     * yok sayılır. 100'ün katı olarak sorulur — gösterim de öyle (docs/09 §1.3).
     */
    maxDistanceKm: optionalPositiveInt,
    // --- Müşteri filtre listesi (Alıcı Paneli.xlsx) --------------------------
    /** Kalori aralığı (kcal/kg) — birincil filtre. */
    calorificMin: optionalPositiveNumber,
    calorificMax: optionalPositiveNumber,
    /** Hazır stok miktarı (ton) — ikincil filtre. */
    quantityMin: optionalPositiveNumber,
    quantityMax: optionalPositiveNumber,
    /** Nem, kül ve azot değerlerine üst sınır — ikincil filtreler. */
    moistureMax: optionalPositiveNumber,
    ashMax: optionalPositiveNumber,
    nitrogenMax: optionalPositiveNumber,
    /** Teslimat formu — çoklu seçim. */
    deliveryForm: csvEnum(DELIVERY_FORMS),
    /** Kırıcı gerekli mi. */
    crusherRequired: optionalBooleanFilter,
    /** Yalnız fotoğraflı/videolu ürünler. */
    hasMedia: optionalBooleanFilter,
    /** Yalnız analiz raporu olanlar — satıcının kendi beyanı. */
    hasAnalysisReport: optionalBooleanFilter,
    /**
     * Yalnız NUMUNEYLE DOĞRULANMIŞ analizi olanlar (docs/10 §2). Beyandan farkı:
     * değeri alıcının laboratuvarı ölçtü ve satıcı yayımlamayı kabul etti.
     */
    hasVerifiedAnalysis: optionalBooleanFilter,
    sort: z.enum(LISTING_SORTS).default('featured'),
});
// ---------------------------------------------------------------------------
// Teklif / RFQ (docs/01 §3.4, docs/04 §Offers)
// ---------------------------------------------------------------------------
const offerNoteSchema = optionalText(1000);
/**
 * Alıcının ilk teklifi. `unitPrice` isteğe bağlıdır: boş bırakılırsa sabit fiyatlı
 * ilanın kendi fiyatı esas alınır, "teklife açık" ilanda satıcı fiyatı karşı teklifle verir.
 */
export const createOfferSchema = z.object({
    listingId: idSchema,
    quantity: decimalInputSchema('Geçerli bir miktar girin.'),
    unitPrice: omittableDecimalSchema('Geçerli bir birim fiyat girin.'),
    deliveryOption: z.enum(DELIVERY_OPTIONS),
    /**
     * Teklifin hangi fabrika için verildiği (docs/09 §1.2). Verilmezse firmanın aktif
     * tesisi kullanılır; sipariş ve teslimat adresi buradan belirlenir.
     */
    facilityId: idSchema.optional(),
    note: offerNoteSchema,
});
/** Karşı teklif: fiyat zorunlu, miktar isteğe bağlı (boşsa mevcut miktar korunur). */
export const counterOfferSchema = z.object({
    unitPrice: decimalInputSchema('Geçerli bir birim fiyat girin.'),
    quantity: omittableDecimalSchema('Geçerli bir miktar girin.'),
    message: offerNoteSchema,
});
/** Ret ve iptalde isteğe bağlı gerekçe — pazarlık geçmişine yazılır. */
export const offerMessageSchema = z.object({ message: offerNoteSchema });
export const offerListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(OFFER_STATUSES).optional(),
});
// ---------------------------------------------------------------------------
// Mesajlaşma (docs/04 §Messages)
// ---------------------------------------------------------------------------
export const messageBodySchema = z
    .string()
    .trim()
    .min(1, 'Mesaj boş olamaz.')
    .max(2000, 'Mesaj en fazla 2000 karakter olabilir.');
export const sendMessageSchema = z.object({ body: messageBodySchema });
/** Konuşma başlatma: aynı ilan + alıcı için konuşma varsa mevcut olan döner. */
export const createConversationSchema = z.object({
    listingId: idSchema,
    body: messageBodySchema.optional(),
});
// ---------------------------------------------------------------------------
// Bildirimler (docs/04 §Notifications)
// ---------------------------------------------------------------------------
/** `?unread=1` — sorgu dizesinden gelen değer metin olabilir. */
const booleanFlagSchema = z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === '1' || value === 'true');
export const notificationListQuerySchema = paginationQuerySchema.extend({
    unread: booleanFlagSchema,
});
// ---------------------------------------------------------------------------
// Sipariş ve ödeme (docs/01 §3.5, docs/04 §Orders, docs/06)
// ---------------------------------------------------------------------------
/** Tekrarlı ya da virgüllü slug listesi: `wasteType=tarimsal-atik,ormansal-atik`. */
const csvSlugs = z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
    if (value === undefined)
        return undefined;
    const list = (Array.isArray(value) ? value : value.split(','))
        .map((item) => item.trim())
        .filter(Boolean);
    return list.length > 0 ? list : undefined;
})
    .refine((list) => list === undefined || list.every((item) => /^[a-z0-9-]{1,80}$/.test(item)), 'Geçersiz filtre değeri.');
const optionalIsoDate = z
    .union([z.literal(''), isoDateSchema])
    .optional()
    .transform((value) => (value === '' ? undefined : value));
/**
 * Siparişlerim filtreleri (müşteri, 17.09.2026). Sipariş VE sevkiyat üzerine:
 *
 * - `wasteType` siparişi süzer (siparişin kendi statüsü yok — müşteri, 17.09.2026).
 * - `shipmentStatus` ve tarih aralığı SEVKİYATI süzer: yalnız eşleşen sevkiyatı olan
 *   siparişler gelir, altlarında da yalnız eşleşen sevkiyatlar listelenir.
 * - Tarih aralığı fiili sevkiyat tarihine göre çalışır, iki uç dahil.
 */
export const orderListQuerySchema = paginationQuerySchema
    .extend({
    /** Atık türü = ürünün ana kategorisinin slug'ı. */
    wasteType: csvSlugs,
    shipmentStatus: csvEnum(ORDER_SHIPMENT_STATUSES),
    dateFrom: optionalIsoDate,
    dateTo: optionalIsoDate,
})
    .refine((value) => !value.dateFrom || !value.dateTo || value.dateFrom <= value.dateTo, {
    path: ['dateTo'],
    error: 'Bitiş tarihi başlangıçtan önce olamaz.',
});
// ---------------------------------------------------------------------------
// Admin (docs/04 §Admin, docs/05 §5)
// ---------------------------------------------------------------------------
/** Onay kuyruğu filtresi — varsayılan olarak bekleyenler gösterilir. */
export const adminCompanyQuerySchema = paginationQuerySchema.extend({
    status: z.enum(APPROVAL_STATUSES).optional(),
    /** Sevkiyat formundaki taşıyıcı seçimi `type=CARRIER&status=APPROVED` ile listeler. */
    type: z.enum(COMPANY_TYPES).optional(),
    q: optionalText(120),
});
export const adminListingQuerySchema = paginationQuerySchema.extend({
    status: z.enum(LISTING_STATUSES).optional(),
    q: optionalText(120),
});
export const adminUserQuerySchema = paginationQuerySchema.extend({
    q: optionalText(120),
    isActive: z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((value) => {
        if (value === undefined || value === '')
            return undefined;
        return value === true || value === '1' || value === 'true';
    }),
});
/** Admin sipariş listesi: sevkiyat statüsü süzgeci (siparişin statüsü yok). */
export const adminOrderQuerySchema = paginationQuerySchema.extend({
    shipmentStatus: z.enum(ORDER_SHIPMENT_STATUSES).optional(),
    q: optionalText(120),
});
/**
 * Sipariş kodu ve tonaj — açılışta ve düzenlemede ortak alanlar (müşteri, 17.09.2026).
 *
 * Kod = firma kodu + fabrika kodu + fabrikanın sipariş numarası. Firma ve fabrika
 * kodu bir kez atanır ve firmaya/fabrikaya yazılır; sonraki siparişte form onları
 * hazır getirir. Fabrika seçildiyse fabrika kodu zorunlu.
 */
const adminOrderFields = {
    facilityId: z
        .union([idSchema, z.literal('')])
        .nullish()
        .transform((value) => value || null),
    categoryId: idSchema,
    totalWeightKg: decimalInputSchema("Toplam ağırlık 0'dan büyük bir sayı olmalıdır (kg)."),
    companyCode: codePartSchema,
    facilityCode: z
        .union([z.literal(''), facilityCodePartSchema])
        .nullish()
        .transform((value) => value || null),
    customerOrderNo: customerOrderNoSchema,
};
const fabrikaKoduZorunlu = (value) => value.facilityId === null || value.facilityCode !== null;
const fabrikaKoduHatasi = {
    path: ['facilityCode'],
    error: 'Fabrika seçtiyseniz fabrika kodunu da girin.',
};
/** Admin sipariş açar: alıcı firma, (varsa) fabrika, ürün, toplam tonaj ve kod. */
export const adminOrderCreateSchema = z
    .object({ buyerCompanyId: idSchema, ...adminOrderFields })
    .refine(fabrikaKoduZorunlu, fabrikaKoduHatasi);
/** Sipariş düzenleme — alıcı firma değişmez. Kod değişirse sevkiyat kodları da yenilenir. */
export const adminOrderUpdateSchema = z
    .object(adminOrderFields)
    .refine(fabrikaKoduZorunlu, fabrikaKoduHatasi);
/**
 * Siparişe sevkiyat girişi — irsaliyeyle açılır ve YOLDA başlar.
 *
 * Taşıyıcı firma isteğe bağlı: seçilirse sevkiyat o taşıyıcının uygulamasındaki
 * "İşlerim" listesine düşer; seçilmezse taşıyıcı uygulamasında görünmez.
 */
export const adminOrderShipmentSchema = z.object({
    plate: plateSchema,
    driverName: personNameSchema,
    driverNationalId: nationalIdSchema,
    shipDate: isoDateSchema,
    /** İrsaliyedeki tahmini ağırlık (kg); kantar fişi gelince net ağırlıkla güncellenir. */
    weightKg: decimalInputSchema("Tahmini ağırlık 0'dan büyük bir sayı olmalıdır (kg)."),
    waybillNo: optionalText(50),
    carrierCompanyId: z
        .union([idSchema, z.literal('')])
        .nullish()
        .transform((value) => value || null),
    /** Malın geldiği üretici — yalnız admin görür; yükleme yeri buradan türer. */
    supplierCompanyId: z
        .union([idSchema, z.literal('')])
        .nullish()
        .transform((value) => value || null),
    /**
     * Sevkiyatın ürünü (07.10.2026): bir siparişte birden çok ürün olabilir. Boş
     * (`''`/`null`) = siparişin ürünü; hiç gönderilmezse düzenlemede değişmez.
     */
    categoryId: z
        .union([idSchema, z.literal('')])
        .nullish()
        .transform((value) => (value === undefined ? undefined : value || null)),
});
/** Sevkiyat düzenleme. TAMAMLANDI yalnız kantar fişi yüklenmiş sevkiyatta seçilebilir. */
export const adminShipmentUpdateSchema = adminOrderShipmentSchema.extend({
    status: z.enum(ORDER_SHIPMENT_STATUSES),
    /** Fabrikaya teslim tarihi — kantar girilirken dolar, burada düzeltilebilir. */
    deliveryDate: z
        .union([z.literal(''), isoDateSchema])
        .nullish()
        .transform((value) => value || null),
});
/** Admin formlarındaki firma listesi: alıcı, satıcı (tedarikçi) ya da taşıyıcı. */
export const adminCompanyOptionsQuerySchema = z.object({
    role: z.enum(['BUYER', 'SELLER', 'CARRIER']),
});
/**
 * Admin'in hızlı tedarikçi (satıcı) kaydı: kullanıcı hesabı açılmaz, firma onaylı
 * doğar ve sevkiyatta tedarikçi olarak seçilir. İl/ilçe yükleme yeridir.
 */
export const adminSupplierCreateSchema = z.object({
    name: companyNameSchema,
    provinceId: z.number().int().positive('İl seçiniz.'),
    districtId: z.number().int().positive('İlçe seçiniz.'),
    phone: phoneSchema,
    taxNumber: z
        .union([z.literal(''), taxNumberSchema])
        .optional()
        .transform((value) => value || undefined),
    address: optionalText(500),
});
/**
 * Kantar fişi: net ağırlık ZORUNLU, belge isteğe bağlı (müşteri: "yüklenen VEYA
 * bilgileri girilen" sevkiyat tamamlanır). Belge multipart gövdede gelir.
 */
export const weighTicketSchema = z.object({
    netWeightKg: decimalInputSchema("Net ağırlık 0'dan büyük bir sayı olmalıdır (kg)."),
    /** Teslim tarihi; boşsa bugün. */
    deliveryDate: z
        .union([z.literal(''), isoDateSchema])
        .optional()
        .transform((value) => value || undefined),
});
/**
 * Admin numune girişi (müşteri, 17.09.2026): ürün türü, gönderim tarihi, satıcı ve
 * alıcının fabrikası. İlana bağlı değil. Satıcı bilgisi alıcıya hiç gösterilmez.
 */
export const adminSampleCreateSchema = z.object({
    categoryId: idSchema,
    sellerCompanyId: idSchema,
    buyerCompanyId: idSchema,
    facilityId: z
        .union([idSchema, z.literal('')])
        .nullish()
        .transform((value) => value || null),
    shippedAt: isoDateSchema,
    note: optionalText(500),
});
/** Admin numune listesi: analiz raporu olan/olmayan süzgeci ve kod/firma araması. */
export const adminSampleQuerySchema = paginationQuerySchema.extend({
    hasReport: optionalBooleanFilter,
    q: optionalText(120),
});
export const adminAuditQuerySchema = paginationQuerySchema.extend({
    action: optionalText(80),
    entity: optionalText(80),
});
/** Ret işlemlerinde gerekçe ZORUNLU: kullanıcıya gösterilir (docs/05 §5). */
export const rejectionSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(1, 'Ret gerekçesi zorunludur.')
        .max(500, 'Ret gerekçesi en fazla 500 karakter olabilir.'),
});
const categoryNameSchema = z
    .string()
    .trim()
    .min(1, 'Kategori adı zorunludur.')
    .max(120, 'Kategori adı en fazla 120 karakter olabilir.');
/** Kategori CRUD — KDV zorunlu, komisyon boş bırakılırsa genel ayar geçerli olur. */
export const adminCategorySchema = z.object({
    name: categoryNameSchema,
    parentId: z
        .union([z.literal(''), idSchema])
        .optional()
        .transform((value) => (value === '' || value === undefined ? undefined : value)),
    description: optionalText(500),
    iconKey: optionalText(40),
    sortOrder: z.coerce.number().int().min(0).max(999).default(0),
    isActive: z.boolean().default(true),
    vatRate: z.coerce.number().min(0).max(100),
    commissionRate: z
        .union([z.literal(''), z.coerce.number().min(0).max(100)])
        .optional()
        .transform((value) => (value === '' || value === undefined ? undefined : Number(value))),
    /** CO₂e hesabı için yaklaşık kalori (kcal/kg). Boş = ana kategorininki (07.10.2026). */
    referenceKcalPerKg: z
        .union([
        z.literal(''),
        z.coerce
            .number()
            .int('Kalori değeri tam sayı olmalıdır.')
            .min(500, 'Kalori değeri en az 500 kcal/kg olmalıdır.')
            .max(10000, 'Kalori değeri en fazla 10.000 kcal/kg olabilir.'),
    ])
        .nullish()
        .transform((value) => (value === '' || value === undefined || value === null ? null : value)),
});
/** `PUT /admin/settings` — yalnızca gönderilen anahtarlar güncellenir. */
export const adminSettingsSchema = z.object({
    commissionRate: z.coerce.number().min(0).max(100).optional(),
    defaultVatRate: z.coerce.number().min(0).max(100).optional(),
    shipmentTrackRadiusKm: z.coerce
        .number()
        .int('Yarıçap tam sayı olmalıdır.')
        .min(1, 'Yarıçap en az 1 km olmalıdır.')
        .max(2000, 'Yarıçap en fazla 2000 km olabilir.')
        .optional(),
    contactInfo: z
        .object({
        email: emailSchema.optional(),
        phone: optionalText(30),
        address: optionalText(300),
    })
        .optional(),
    announcementBanner: z
        .object({
        enabled: z.boolean(),
        text: optionalText(300),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// Değerlendirmeler (docs/01 §3.6, docs/04 §Reviews)
// ---------------------------------------------------------------------------
export const createReviewSchema = z.object({
    orderId: idSchema,
    rating: z.coerce
        .number()
        .int()
        .min(1, 'Puan 1 ile 5 arasında olmalıdır.')
        .max(5, 'Puan 1 ile 5 arasında olmalıdır.'),
    comment: optionalText(1000),
});
export const adminReviewQuerySchema = paginationQuerySchema.extend({
    status: z.enum(APPROVAL_STATUSES).optional(),
});
// ---------------------------------------------------------------------------
// İçerik: statik sayfalar, blog, iletişim (docs/04 §Content)
// ---------------------------------------------------------------------------
const contentSlugSchema = z
    .string()
    .trim()
    .min(1, 'Adres zorunludur.')
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Adres yalnızca küçük harf, rakam ve tire içerebilir.');
export const adminPageSchema = z.object({
    slug: contentSlugSchema,
    title: z.string().trim().min(1, 'Başlık zorunludur.').max(160),
    body: z.string().trim().min(1, 'İçerik zorunludur.').max(50_000),
    isPublished: z.boolean().default(false),
});
export const adminBlogPostSchema = z.object({
    slug: contentSlugSchema,
    title: z.string().trim().min(1, 'Başlık zorunludur.').max(160),
    excerpt: optionalText(300),
    body: z.string().trim().min(1, 'İçerik zorunludur.').max(50_000),
    coverUrl: optionalText(500),
    /** Boş bırakılırsa taslak kalır; doldurulunca yayınlanır. */
    isPublished: z.boolean().default(false),
});
/** İletişim formu — oturum gerektirmez, hız sınırlıdır. */
export const contactMessageSchema = z.object({
    name: z.string().trim().min(1, 'Ad soyad zorunludur.').max(120),
    email: emailSchema,
    subject: z.string().trim().min(1, 'Konu zorunludur.').max(160),
    message: z
        .string()
        .trim()
        .min(1, 'Mesaj zorunludur.')
        .max(2000, 'Mesaj en fazla 2000 karakter olabilir.'),
});
// ---------------------------------------------------------------------------
// Alım talebi (docs/10 §1)
// ---------------------------------------------------------------------------
/**
 * Alıcının açtığı talep. İlan formunun aynadaki görüntüsü: satıcı "şu var" der,
 * alıcı "şunu arıyorum" der. Bu yüzden alanlar ilan filtrelerinin TAVAN/TABAN hâli —
 * aranan asgari kalori, kabul edilen en yüksek nem/kül/azot.
 */
export const demandBaseSchema = z.object({
    title: z
        .string()
        .trim()
        .min(1, 'Başlık zorunludur.')
        .max(160, 'Başlık en fazla 160 karakter olabilir.'),
    description: z
        .string()
        .trim()
        .min(1, 'Açıklama zorunludur.')
        .max(5000, 'Açıklama en fazla 5000 karakter olabilir.'),
    categoryId: idSchema,
    quantity: decimalInputSchema('Geçerli bir miktar girin.'),
    unit: z.enum(UNITS),
    recurrence: z.enum(RECURRENCES),
    calorificMin: optionalPositiveIntSchema,
    moistureMax: omittableDecimalSchema('Nem oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Nem oranı 0 ile 100 arasında olmalıdır.'),
    ashMax: omittableDecimalSchema('Kül oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Kül oranı 0 ile 100 arasında olmalıdır.'),
    nitrogenMax: omittableDecimalSchema('Azot oranı 0 ile 100 arasında olmalıdır.').refine((value) => value === undefined || Number(value) <= 100, 'Azot oranı 0 ile 100 arasında olmalıdır.'),
    deliveryForms: z.array(z.enum(DELIVERY_FORMS)).max(DELIVERY_FORMS.length).optional(),
    crusherAvailable: z.boolean().optional(),
    maxDistanceKm: optionalPositiveIntSchema,
    targetUnitPrice: omittableDecimalSchema('Geçerli bir birim fiyat girin.'),
    deliveryOption: z.enum(DELIVERY_OPTIONS),
    /**
     * Malın teslim edileceği tesis. Verilmezse firmanın aktif tesisi kullanılır;
     * il/ilçe de oradan çözülür — alıcı ayrıca konum girmez.
     */
    facilityId: idSchema.optional(),
    /** Talebin açık kalacağı gün sayısı. */
    validityDays: z.coerce.number().int().min(1).max(180).optional(),
});
export const createDemandSchema = demandBaseSchema;
export const updateDemandSchema = demandBaseSchema;
/** Talebi kapatma: gerekçe isteğe bağlı ama satıcılara bildirim olarak gider. */
export const closeDemandSchema = z.object({ reason: optionalText(500) });
export const demandSearchQuerySchema = paginationQuerySchema.extend({
    q: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((value) => (value === undefined || value === '' ? undefined : value)),
    categoryId: idSchema.optional(),
    categorySlug: z.string().trim().max(160).optional(),
    provinceId: optionalPositiveInt,
    status: z.enum(DEMAND_STATUSES).optional(),
    /** Satıcının konumundan talebe en fazla uzaklık (km). */
    maxDistanceKm: optionalPositiveInt,
    quantityMin: optionalPositiveNumber,
    quantityMax: optionalPositiveNumber,
    /** Satıcının ürünü bu kaloriyi tutturabiliyorsa gösterilsin. */
    calorificMax: optionalPositiveNumber,
    deliveryForm: csvEnum(DELIVERY_FORMS),
    sort: z.enum(DEMAND_SORTS).default('newest'),
});
/**
 * Satıcının talebe cevabı. Cevap ayrı bir varlık değil, `Offer`'ın kendisidir —
 * bu yüzden hangi ilanla cevap verildiği ZORUNLU: alıcı teklifin arkasındaki ürünün
 * nem/kalori/konum bilgisini görebilmeli, yoksa teklif sadece bir rakam olur.
 */
export const demandBidSchema = z.object({
    listingId: idSchema,
    quantity: decimalInputSchema('Geçerli bir miktar girin.'),
    unitPrice: decimalInputSchema('Geçerli bir birim fiyat girin.'),
    note: optionalText(1000),
});
// ---------------------------------------------------------------------------
// Numune ve analiz (docs/10 §2)
// ---------------------------------------------------------------------------
/** Alıcının numune talebi. İlan için zorunlu değil — isteyen alıcı ister. */
export const sampleRequestSchema = z.object({
    listingId: idSchema,
    /** Numunenin gönderileceği tesis; verilmezse aktif tesis. */
    facilityId: idSchema.optional(),
    note: optionalText(1000),
});
/**
 * Satıcının gönderim bildirimi. Kargoysa takip numarası, elden teslimse kime
 * teslim edildiği istenir — Çimsa portalındaki ayrımın aynısı.
 */
export const sampleShipSchema = z
    .object({
    deliveryMethod: z.enum(SAMPLE_DELIVERIES),
    trackingNo: optionalText(80),
    handedTo: optionalText(160),
})
    .refine((value) => value.deliveryMethod !== 'KARGO' || !!value.trackingNo, {
    path: ['trackingNo'],
    error: 'Kargo ile gönderimde takip numarası zorunludur.',
})
    .refine((value) => value.deliveryMethod !== 'ELDEN' || !!value.handedTo, {
    path: ['handedTo'],
    error: 'Elden teslimde kime teslim edildiğini yazın.',
});
/** Alıcının laboratuvar sonucu. Tek bir değer bile girilmezse kayıt anlamsız olur. */
export const sampleResultSchema = z
    .object({
    calorificValue: optionalPositiveIntSchema,
    moisturePct: omittableDecimalSchema('Nem oranı 0 ile 100 arasında olmalıdır.'),
    ashPct: omittableDecimalSchema('Kül oranı 0 ile 100 arasında olmalıdır.'),
    chloridePct: omittableDecimalSchema('Klor oranı 0 ile 100 arasında olmalıdır.'),
    sulfurPct: omittableDecimalSchema('Kükürt oranı 0 ile 100 arasında olmalıdır.'),
    nitrogenPct: omittableDecimalSchema('Azot oranı 0 ile 100 arasında olmalıdır.'),
    verdict: z.enum(SAMPLE_VERDICTS),
    // Rapor bağlantısı yok (müşteri, 18.09.2026): rapor dosya olarak yüklenir,
    // `POST /samples/:id/report`. Serbest adres alanı da kalkmış oldu.
    note: optionalText(2000),
})
    .refine((value) => value.calorificValue !== undefined ||
    value.moisturePct !== undefined ||
    value.ashPct !== undefined ||
    value.chloridePct !== undefined ||
    value.sulfurPct !== undefined ||
    value.nitrogenPct !== undefined, { path: ['calorificValue'], error: 'En az bir ölçüm değeri girin.' });
/** Satıcının sonucu ilanında yayımlama kararı. */
export const samplePublishSchema = z.object({ isPublished: z.boolean() });
export const sampleCancelSchema = z.object({ reason: optionalText(500) });
export const sampleListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(SAMPLE_STATUSES).optional(),
    /**
     * `all` (varsayılan): firmanın taraf olduğu tüm numuneler — ekranda tek "Numuneler"
     * listesi (müşteri, 18.09.2026). `incoming` / `outgoing` mobil için duruyor.
     */
    direction: z.enum(['all', 'incoming', 'outgoing']).default('all'),
});
// ---------------------------------------------------------------------------
// Fiyat talebi (müşteri, 23.09.2026)
// ---------------------------------------------------------------------------
/**
 * Alıcının "bu üründen fiyat verin" talebi. Miktar, periyot ve beklenti tek bir
 * serbest notta toplanıyor (müşteri kararı): telefonla gelen talep de aynı
 * cümlelerle geliyor, form doldurtmak alıcıyı yavaşlatıyordu.
 */
export const priceRequestSchema = z.object({
    sampleId: z.string().min(1, 'Numune seçilmelidir.'),
    note: optionalText(2000),
});
/** Admin fiyat talebi listesi. */
export const adminPriceRequestQuerySchema = paginationQuerySchema.extend({
    q: optionalText(120),
});
// ---------------------------------------------------------------------------
// Sevkiyat ve taşıyıcı (docs/11 §2)
// ---------------------------------------------------------------------------
/** Sevkiyat ilanı — Biyolink (admin) açar. */
export const shipmentSchema = z
    .object({
    title: z
        .string()
        .trim()
        .min(1, 'Başlık zorunludur.')
        .max(160, 'Başlık en fazla 160 karakter olabilir.'),
    description: optionalText(2000),
    categoryId: idSchema,
    /** Siparişe bağlı sevkiyat; boşsa serbest ilan. */
    orderId: idSchema.optional(),
    /**
     * Malı bekleyen alıcı — canlı takibi görecek taraf. Siparişe bağlı sevkiyatta
     * verilmezse siparişin alıcısından devralınır.
     */
    buyerCompanyId: idSchema.optional(),
    quantity: decimalInputSchema('Geçerli bir miktar girin.'),
    unit: z.enum(UNITS),
    fromProvinceId: z.number().int().positive('Yükleme ili seçiniz.'),
    fromDistrictId: z.number().int().positive('Yükleme ilçesi seçiniz.'),
    fromAddress: optionalText(500),
    fromContact: optionalText(160),
    toProvinceId: z.number().int().positive('Teslim ili seçiniz.'),
    toDistrictId: z.number().int().positive('Teslim ilçesi seçiniz.'),
    toAddress: optionalText(500),
    toContact: optionalText(160),
    pickupFrom: z.coerce.date(),
    pickupTo: z.coerce.date(),
    deliverBy: z.coerce.date().optional(),
    vehicleType: z.enum(VEHICLE_TYPES).optional(),
    offeredPrice: omittableDecimalSchema('Geçerli bir ücret girin.'),
})
    .refine((value) => value.pickupTo >= value.pickupFrom, {
    path: ['pickupTo'],
    error: 'Yükleme bitiş tarihi başlangıçtan önce olamaz.',
})
    .refine((value) => !value.deliverBy || value.deliverBy >= value.pickupFrom, {
    path: ['deliverBy'],
    error: 'Teslim tarihi yükleme tarihinden önce olamaz.',
});
export const shipmentSearchQuerySchema = paginationQuerySchema.extend({
    status: z.enum(SHIPMENT_STATUSES).optional(),
    fromProvinceId: optionalPositiveInt,
    toProvinceId: optionalPositiveInt,
    vehicleType: csvEnum(VEHICLE_TYPES),
    /** `open`: başvuruya açık ilanlar. `mine`: taşıyıcının üstlendiği işler. */
    scope: z.enum(['open', 'mine']).default('open'),
});
/** Taşıyıcının sevkiyata talip olması. */
export const shipmentApplicationSchema = z.object({
    price: omittableDecimalSchema('Geçerli bir ücret girin.'),
    note: optionalText(1000),
    availableAt: z.coerce.date().optional(),
});
/** Atama: hangi başvuru kabul edilecek ve araç/şoför kim. */
export const shipmentAssignSchema = z.object({
    applicationId: idSchema,
    driverName: optionalText(160),
    driverPhone: optionalText(30),
    plate: optionalText(20),
});
/** Şoförün araç bilgisini kendi girmesi (atamadan sonra değişebilir). */
export const shipmentVehicleSchema = z.object({
    driverName: optionalText(160),
    driverPhone: optionalText(30),
    plate: optionalText(20),
});
export const shipmentCancelSchema = z.object({ reason: optionalText(500) });
/**
 * Konum paylaşımı rızası. Ayrı bir uç, çünkü KVKK açısından kaydedilmesi gereken
 * bir irade beyanı: şoför istediği an kapatabilir.
 */
export const locationSharingSchema = z.object({ enabled: z.boolean() });
const locationPointSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    speedKph: z.number().min(0).max(300).optional(),
    headingDeg: z.number().min(0).max(360).optional(),
    accuracyM: z.number().min(0).optional(),
    recordedAt: z.coerce.date(),
});
/**
 * Konum bildirimi. Dizi olarak alınır: şebekesiz bölgede biriken noktalar bağlantı
 * gelince tek istekte gönderilsin — kamyon rotasının yarısı boş kalmasın.
 */
export const locationReportSchema = z.object({
    points: z.array(locationPointSchema).min(1, 'En az bir konum gerekir.').max(LOCATION_BATCH_MAX),
});
//# sourceMappingURL=schemas.js.map